import { useState } from 'react';
import { Field, money } from './fields';
import EmailConfirmation, { emailsMatch } from './EmailConfirmation';
import { businessDateISO, formatDate } from '../../service/dateFormatting';

const initialPayment = (receiptEmail) => ({
    paidAt: businessDateISO(),
    paymentMethod: 'Check',
    paymentReference: '',
    receiptEmail,
});

export default function OrderPayment({
    api, applyOrder, busy, order, orderId, run, setMessage,
}) {
    const [payment, setPayment] = useState(() => initialPayment(order.billingEmail));
    const [emailConfirmation, setEmailConfirmation] = useState('');
    const needsEmailConfirmation = !emailsMatch(payment.receiptEmail, order.billingEmail);
    const emailConfirmed = !needsEmailConfirmation
        || emailsMatch(payment.receiptEmail, emailConfirmation);

    const markPaid = (event) => {
        event.preventDefault();
        if (!emailConfirmed) return;
        run(async () => {
            try {
                const data = await api(`/orders/${orderId}/payment`, {
                    method: 'POST',
                    body: payment,
                });
                applyOrder(data.order);
                setMessage(data.warnings.length
                    ? `Full payment recorded. ${data.warnings.join(' ')}`
                    : 'Full payment recorded. The receipt email was sent.');
            } catch (requestError) {
                // Payment may have committed even if its response was lost.
                try {
                    const fresh = await api(`/orders/${orderId}`);
                    applyOrder(fresh);
                    if (fresh.paymentStatus === 'paid') {
                        setMessage('Full payment is recorded. Review the receipt status in email history.');
                    }
                } catch {
                    // Preserve the payment error if refreshing also fails.
                }
                throw requestError;
            }
        });
    };

    return (
        <div className="po-panel">
            <h3>Payment</h3>
            {order.paymentStatus === 'paid' ? (
                <p>
                    {`Paid in full on ${formatDate(order.paidAt)} via ${order.paymentMethod}. `}
                    {`Reference: ${order.paymentReference || '—'}`}
                </p>
            ) : (
                <form onSubmit={markPaid}>
                    <p>
                        {`Record payment only once the full ${money(order.amountCents)}`}
                        {' '}
                        has been received. Recording payment immediately sends a receipt
                        to the address below and does not change access.
                    </p>
                    <div className="po-grid">
                        <Field
                            label="Payment date"
                            name="paidAt"
                            values={payment}
                            setValues={setPayment}
                            type="date"
                            required
                            max={businessDateISO()} />
                        <Field
                            label="Payment method"
                            name="paymentMethod"
                            values={payment}
                            setValues={setPayment}
                            required
                            maxLength={100} />
                        <Field
                            label="Payment reference (optional)"
                            name="paymentReference"
                            values={payment}
                            setValues={setPayment}
                            maxLength={200} />
                        <Field
                            label="Receipt recipient email"
                            name="receiptEmail"
                            values={payment}
                            setValues={setPayment}
                            type="email"
                            maxLength={254}
                            required />
                        {needsEmailConfirmation && (
                            <EmailConfirmation
                                label="Confirm receipt recipient email"
                                email={payment.receiptEmail}
                                value={emailConfirmation}
                                onChange={setEmailConfirmation} />
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={busy || !emailConfirmed}>
                        Record full payment & send receipt
                    </button>
                </form>
            )}
        </div>
    );
}
